"use client"

import { useEffect, useState } from "react"
import { Menu, Search, User, ChevronDown } from "lucide-react"
import { ThemeSwitcher } from "./theme-switcher"
import { FontSwitcher } from "./font-switcher"
import { SearchModal } from "./search-modal"
import logoScroll from "@/public/logoScroll.png"
import Image from "next/image"
import { useTranslations } from 'next-intl'
import { Link } from "@/i18n/navigation"
import { 
  Hotel, 
  UtensilsCrossed, 
  Calendar, 
  PartyPopper, 
  HardHat,
  MapPin, 
  Camera, 
  Plane, 
  Building2,
  TreePine,
  Car,
  Compass,
  Users,
  Waves,
  Mountain,
  ShoppingBag,
  Music,
  Gamepad2,
  Tent,
  Ship
} from "lucide-react"

export function ScrollHeader() {
  const t = useTranslations()
  const [isVisible, setIsVisible] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false)

  // All services from services page
  const allServices = [
    { icon: UtensilsCrossed, titleKey: "navigation.restaurants", link: "/restaurants" },
    { icon: Calendar, titleKey: "navigation.events", link: "/events" },
    { icon: PartyPopper, titleKey: "navigation.festivals", link: "/festivals" },
    { icon: Compass, titleKey: "navigation.activities", link: "/activities" },
    { icon: Building2, titleKey: "navigation.museums", link: "/museums" },
    { icon: Camera, titleKey: "navigation.monuments", link: "/monuments" },
    { icon: Plane, titleKey: "navigation.circuits", link: "/circuits" },
    { icon: TreePine, titleKey: "navigation.states", link: "/states" },
    { icon: HardHat, titleKey: "navigation.artisanats", link: "/artisanats" },

    { icon: Building2, titleKey: "navigation.guestHouses", link: "/guest-houses" },
    { icon: Users, titleKey: "navigation.medicalStays", link: "/medical-stays" },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 100)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest("[data-user-menu]")) {
        setUserMenuOpen(false)
      }
      if (!target.closest("[data-services-menu]")) {
        setServicesMenuOpen(false)
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  return (
    <>
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      
      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full z-50 transform transition-transform duration-300 md:hidden ${
          sidebarOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          background: `linear-gradient(135deg, var(--card) 0%, var(--card) 100%)`,
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Header with Close Button */}
        <div className="flex items-center justify-between px-6 py-6 border-b border-border">
          <Link href="/" className="block hover:cursor-pointer">
          <Image
            src={logoScroll || "/placeholder.svg"}
            height="80"
            width="120"
            alt="Carthago guide"
          />
          </Link>

          <button
            onClick={() => setSidebarOpen(false)}
            className="text-foreground hover:text-primary transition-colors p-2 rounded-lg hover:bg-muted/50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation Links - All Services */}
        <nav className="flex flex-col gap-4 px-6 py-8 overflow-y-auto max-h-[calc(100vh-200px)] scrollbar-thin scrollbar-thumb-primary/30 scrollbar-track-transparent">
          <div className="grid grid-cols-2 gap-3 pr-2">
            {allServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Link
                  key={index}
                  href={service.link}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    <IconComponent className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors text-center">
                    {t(service.titleKey)}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Settings Section */}
        <div className="px-6 py-4 border-t border-border mt-auto">
          <div className="mb-6">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Font</p>
            <FontSwitcher />
          </div>
          <div>
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Theme</p>
            <ThemeSwitcher />
          </div>
        </div>
      </div>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isVisible ? "bg-card/95 backdrop-blur-md shadow-lg border-b border-border" : "transparent"
        }`}
      >
        <div
          className={`max-w-7xl mx-auto px-4 transition-all duration-300 ${isVisible ? "py-5" : "py-0 opacity-0 pointer-events-none"}`}
        >
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-foreground">
              <Image
                src={logoScroll || "/placeholder.svg"}
                height="100"
                width="160"
                alt="Carthago guide"
                className="-mt-2"
              />
            </h1>
            <div className="hidden md:flex items-center gap-8">
              <Link href='/' className="text-sm text-foreground hover:text-primary cursor-pointer transition-colors">{t('navigation.home')}</Link>
              <Link href="/destinations" className="text-sm text-foreground hover:text-primary cursor-pointer transition-colors">
                {t('navigation.destinations')}
              </Link>
              <Link href="/contact" className="text-sm text-foreground hover:text-primary cursor-pointer transition-colors">{t('navigation.contact')}</Link>
              
              {/* Services Dropdown */}
              <div className="relative" data-services-menu>
                <button
                  onClick={() => setServicesMenuOpen(!servicesMenuOpen)}
                  className="flex items-center gap-1 text-sm text-foreground hover:text-primary transition-colors"
                >
                  <span>{t('common.services')}</span>
                  <svg className={`w-4 h-4 transition-transform ${servicesMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Services Dropdown Menu */}
                {servicesMenuOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-xl shadow-lg backdrop-blur-sm z-50 max-h-96 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2 p-4">
                      {allServices.map((service, index) => {
                        const IconComponent = service.icon;
                        return (
                          <Link
                            key={index}
                            href={service.link}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group"
                            onClick={() => setServicesMenuOpen(false)}
                          >
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                              <IconComponent className="w-5 h-5 text-primary" />
                            </div>
                            <span className="text-[12.5px] font-medium text-foreground group-hover:text-primary transition-colors">
                              {t(service.titleKey)}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSearchOpen(true)}
                className="text-foreground hover:text-primary transition-colors p-2 rounded-lg hover:bg-muted/50"
              >
                <Search className="w-5 h-5" />
              </button>

              <div className="relative" data-user-menu>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 text-foreground hover:text-primary transition-colors p-2 rounded-lg hover:bg-muted/50"
                >
                  <User className="w-5 h-5" />
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* User Menu Dropdown */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-lg py-3 backdrop-blur-sm z-50">
                    {/* Font Section */}
                    <div className="px-4 py-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Font</p>
                      <div onClick={(e) => e.stopPropagation()}>
                        <FontSwitcher />
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="my-2 border-t border-border" />

                    {/* Theme Section */}
                    <div className="px-4 py-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Theme</p>
                      <div onClick={(e) => e.stopPropagation()}>
                        <ThemeSwitcher />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)} 
                className="md:hidden text-foreground hover:text-primary transition-colors p-2 rounded-lg hover:bg-muted/50"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}
